
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { AppState, Page, Section, SiteConfig, SectionType, Theme, CartItem, Order, OrderStatus } from '../types';
import { INITIAL_STATE } from '../constants';
import { loadRemoteContent, saveRemoteContent } from '../services/storageService';

interface AppContextType extends AppState {
  isAdminLoggedIn: boolean; // New auth state
  login: (token: string) => Promise<{success: boolean, error?: string}>;
  logout: () => void;
  
  setPrimaryColor: (color: string) => void;
  setSecondaryColor: (color: string) => void;
  updateConfig: (updates: Partial<SiteConfig>) => void;
  toggleEditMode: () => void;
  updatePage: (pageId: string, updates: Partial<Page>) => void;
  updateSection: (pageId: string, sectionId: string, updates: Partial<Section>) => void;
  // Product Helper
  updateProduct: (productId: string, updates: any) => void; 
  duplicateProduct: (productId: string) => string | null; 
  getProductById: (productId: string) => { product: any; sectionId: string; pageId: string } | null;
  getAllProducts: () => any[]; 
  
  addPage: (title: string, slug: string, type?: 'standard' | 'technology' | 'downloads' | 'contact' | 'portfolio') => void;
  addSection: (pageId: string, type: SectionType) => void;
  deleteSection: (pageId: string, sectionId: string) => void;
  deletePage: (pageId: string) => void;
  loadState: (newState: AppState) => void;
  // Theme methods
  applyTheme: (themeId: string) => void;
  addTheme: (name: string) => void;
  deleteTheme: (themeId: string) => void;
  // Cart methods
  addToCart: (item: CartItem) => void;
  removeFromCart: (itemId: string) => void;
  toggleCart: () => void;
  clearCart: () => void;
  // Order methods
  fetchOrders: () => Promise<void>;
  updateOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
  // Import & Restore
  validateCSVImport: (csvContent: string) => { success: boolean, errors: string[], warnings: string[], parsedCount: number, data: any };
  applyCSVImport: (data: any, mode: 'replace' | 'append') => void;
  restoreDefaults: () => void;
  // Server Sync
  syncWithServer: () => Promise<boolean>;
  isLoading: boolean;
  
  // User Features
  favorites: string[];
  recentlyViewed: string[];
  toggleFavorite: (productId: string) => void;
  addRecentlyViewed: (productId: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>(INITIAL_STATE);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // User local features (favorites, recently viewed)
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('site_favorites');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const [recentlyViewed, setRecentlyViewed] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('site_recently_viewed');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('site_favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem('site_recently_viewed', JSON.stringify(recentlyViewed));
  }, [recentlyViewed]);

  const toggleFavorite = (productId: string) => {
    setFavorites(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const addRecentlyViewed = (productId: string) => {
    setRecentlyViewed(prev => {
      const updated = prev.filter(id => id !== productId);
      updated.unshift(productId);
      return updated.slice(0, 10); // keep last 10
    });
  };
  
  // Session Management
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const heartbeatInterval = useRef<any>(null);

  // Load from Server on Mount
  useEffect(() => {
    const init = async () => {
        setIsLoading(true);
        try {
            const remoteState = await loadRemoteContent();
            
            if (remoteState) {
                console.log("Loaded content from server (CMS Mode)");
                setState(prev => ({
                    ...prev,
                    ...remoteState,
                    cart: prev.cart // Keep cart
                }));

                // Check for existing session token in sessionStorage (Client Side only)
                // Note: We don't auto-reconnect session here because we need to re-verify lock with server.
                // We just keep the token if user refreshes, but they might need to re-login if lock expired.
            }
        } catch (e) {
             // Fallback on error too
             console.error("Init error", e);
        }
        setIsLoading(false);
    };
    init();
  }, []);

  // Heartbeat Logic
  useEffect(() => {
      if (isAdminLoggedIn && sessionToken) {
          // Send heartbeat immediately
          sendHeartbeat(sessionToken);

          // And then every 30 seconds
          heartbeatInterval.current = setInterval(() => {
              sendHeartbeat(sessionToken);
          }, 30000);
      } else {
          if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
      }

      return () => {
          if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
      };
  }, [isAdminLoggedIn, sessionToken]);

  const sendHeartbeat = async (sToken: string) => {
      try {
          const res = await fetch('api/auth.php', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({ action: 'heartbeat', sessionToken: sToken })
          });
          const data = await res.json();
          if (!data.success) {
              console.warn("Heartbeat failed, logging out:", data.error);
              logout(false); // Logout locally without calling server (since session is dead)
              alert("Twoja sesja wygasła lub inny administrator zalogował się.");
          }
      } catch (e) {
          console.error("Heartbeat network error", e);
      }
  };

  // Auth Methods
  const login = async (token: string): Promise<{success: boolean, error?: string}> => {
      try {
          const res = await fetch('api/auth.php', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({ action: 'login', token: token })
          });
          
          let data;
          try {
              const text = await res.text();
              data = JSON.parse(text);
          } catch (parseError) {
              // Fallback for demo/dev mode if API doesn't exist or Vite serves raw PHP
              const validToken = state.config.adminToken;
              if (!validToken || token === validToken) {
                  setIsAdminLoggedIn(true);
                  sessionStorage.setItem('adminToken', token);
                  setState(prev => ({ ...prev, isEditMode: true, viewMode: 'admin' }));
                  return { success: true };
              }
              return { success: false, error: 'Nieprawidłowe hasło (tryb lokalny).' };
          }

          if (data && data.success) {
              setIsAdminLoggedIn(true);
              setSessionToken(data.sessionToken);
              sessionStorage.setItem('adminToken', token); // Store main token for saves
              setState(prev => ({ ...prev, isEditMode: true, viewMode: 'admin' }));
              return { success: true };
          } else if (data) {
              return { success: false, error: data.error || 'Błąd logowania.' };
          }
           
          return { success: false, error: 'Błąd logowania.' };

      } catch (e) {
          console.error("Login error", e);
          
          // Network error fallback
          const validToken = state.config.adminToken;
          if (!validToken || token === validToken) {
              setIsAdminLoggedIn(true);
              sessionStorage.setItem('adminToken', token);
              setState(prev => ({ ...prev, isEditMode: true, viewMode: 'admin' }));
              return { success: true };
          }
          
          return { success: false, error: 'Błąd sieci lub błędne hasło.' };
      }
  };

  const logout = async (callServer = true) => {
      if (callServer && sessionToken) {
          try {
              fetch('api/auth.php', {
                  method: 'POST',
                  body: JSON.stringify({ action: 'logout', sessionToken })
              });
          } catch(e) {}
      }

      setIsAdminLoggedIn(false);
      setSessionToken(null);
      sessionStorage.removeItem('adminToken');
      setState(prev => ({ ...prev, isEditMode: false, viewMode: 'public' }));
      if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
  };

  // Apply colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', state.config.primaryColor);
    root.style.setProperty('--color-secondary', state.config.secondaryColor);
  }, [state.config.primaryColor, state.config.secondaryColor]);
  
  const setPrimaryColor = (color: string) => {
    setState(prev => ({ ...prev, config: { ...prev.config, primaryColor: color } }));
  };

  const setSecondaryColor = (color: string) => {
    setState(prev => ({ ...prev, config: { ...prev.config, secondaryColor: color } }));
  };

  const updateConfig = (updates: Partial<SiteConfig>) => {
    setState(prev => ({ ...prev, config: { ...prev.config, ...updates } }));
  };

  const applyTheme = (themeId: string) => {
    const theme = state.themes.find(t => t.id === themeId);
    if (theme) {
      setState(prev => ({
        ...prev,
        currentThemeId: themeId,
        config: { ...prev.config, primaryColor: theme.primaryColor, secondaryColor: theme.secondaryColor }
      }));
    }
  };

  const addTheme = (name: string) => {
    const newTheme: Theme = {
      id: `theme-${Date.now()}`,
      name: name,
      primaryColor: state.config.primaryColor,
      secondaryColor: state.config.secondaryColor,
      isPreset: false
    };
    setState(prev => ({ ...prev, themes: [...prev.themes, newTheme], currentThemeId: newTheme.id }));
  };

  const deleteTheme = (themeId: string) => {
    setState(prev => {
      const defaultTheme = prev.themes[0];
      return {
        ...prev,
        themes: prev.themes.filter(t => t.id !== themeId),
        currentThemeId: prev.currentThemeId === themeId ? defaultTheme.id : prev.currentThemeId
      };
    });
  };

  const addToCart = (newItem: CartItem) => {
    setState(prev => {
      const existingItemIndex = prev.cart.findIndex(i => i.productId === newItem.productId && i.variantName === newItem.variantName);
      let newCart = [...prev.cart];
      if (existingItemIndex > -1) {
          newCart[existingItemIndex] = { ...newCart[existingItemIndex], quantity: newCart[existingItemIndex].quantity + newItem.quantity };
      } else {
          newCart.push(newItem);
      }
      return { ...prev, cart: newCart, isCartOpen: true };
    });
  };

  const removeFromCart = (itemId: string) => setState(prev => ({ ...prev, cart: prev.cart.filter(i => i.id !== itemId) }));
  const toggleCart = () => setState(prev => ({ ...prev, isCartOpen: !prev.isCartOpen }));
  const clearCart = () => setState(prev => ({ ...prev, cart: [] }));

  const fetchOrders = async () => {
      try {
          const token = state.config.adminToken || '';
          const response = await fetch(`api/orders.php?token=${token}`);
          if (response.ok) {
              const data = await response.json();
              if (data.success && Array.isArray(data.orders)) {
                  setState(prev => ({ ...prev, orders: data.orders }));
              }
          }
      } catch (e) { console.warn("Fetch orders failed", e); }
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus) => {
      try {
          const token = state.config.adminToken || '';
          await fetch(`api/order_update.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token, orderId, status })
          });
          setState(prev => ({
              ...prev,
              orders: prev.orders.map(o => o.id === orderId || o.extOrderId === orderId ? { ...o, status } : o)
          }));
      } catch (e) { console.error(e); }
  };

  const syncWithServer = async () => {
      const result = await saveRemoteContent(state);
      if (result.success) return true;
      alert(`Błąd zapisu: ${result.error}`);
      return false;
  };

  const toggleEditMode = () => {
      if (isAdminLoggedIn) {
          setState(prev => ({ ...prev, isEditMode: !prev.isEditMode }));
      }
  };

  const updatePage = (pageId: string, updates: Partial<Page>) => {
    setState(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id === pageId ? { ...p, ...updates } : p)
    }));
  };

  const updateSection = (pageId: string, sectionId: string, updates: Partial<Section>) => {
    setState(prev => ({
      ...prev,
      pages: prev.pages.map(p => {
        if (p.id !== pageId) return p;
        return {
          ...p,
          sections: p.sections.map(s => s.id === sectionId ? { ...s, ...updates } : s)
        };
      })
    }));
  };
  
  const getProductById = (productId: string) => {
      for (const page of state.pages) {
          for (const section of page.sections) {
              if (section.items) {
                  const product = section.items.find(item => item.id === productId);
                  if (product) return { product, sectionId: section.id, pageId: page.id };
              }
          }
      }
      return null;
  };

  const getAllProducts = () => {
      const allProducts: any[] = [];
      for (const page of state.pages) {
          for (const section of page.sections) {
              if (section.items && section.type === SectionType.PRODUCT_GRID) allProducts.push(...section.items);
          }
      }
      return allProducts;
  };

  const updateProduct = (productId: string, updates: any) => {
    setState(prev => {
        const newPages = prev.pages.map(page => ({
            ...page,
            sections: page.sections.map(section => {
                if (!section.items) return section;
                const itemIndex = section.items.findIndex(i => i.id === productId);
                if (itemIndex === -1) return section;
                const newItems = [...section.items];
                newItems[itemIndex] = { ...newItems[itemIndex], ...updates };
                return { ...section, items: newItems };
            })
        }));
        return { ...prev, pages: newPages };
    });
  };

  const duplicateProduct = (productId: string): string | null => {
      let newId = null;
      setState(prev => {
          const newPages = prev.pages.map(page => ({
              ...page,
              sections: page.sections.map(section => {
                  if (!section.items) return section;
                  const itemIndex = section.items.findIndex(i => i.id === productId);
                  if (itemIndex === -1) return section;
                  const newItem = JSON.parse(JSON.stringify(section.items[itemIndex]));
                  newItem.id = `prod-${Date.now()}`;
                  newItem.title += ' (Kopia)';
                  newId = newItem.id;
                  return { ...section, items: [...section.items, newItem] };
              })
          }));
          return { ...prev, pages: newPages };
      });
      return newId;
  };

  const addPage = (title: string, slug: string) => {
      const newPage: Page = {
          id: `page-${Date.now()}`,
          slug, title, metaDescription: title, sections: []
      };
      setState(prev => ({ ...prev, pages: [...prev.pages, newPage] }));
  };

  const addSection = (pageId: string, type: SectionType) => {
      setState(prev => ({
          ...prev,
          pages: prev.pages.map(p => p.id === pageId ? { 
              ...p, 
              sections: [...p.sections, { id: `sec-${Date.now()}`, type, title: 'Nowa Sekcja' }] 
          } : p)
      }));
  };

  const deleteSection = (pageId: string, sectionId: string) => {
      setState(prev => ({
          ...prev,
          pages: prev.pages.map(p => p.id === pageId ? { ...p, sections: p.sections.filter(s => s.id !== sectionId) } : p)
      }));
  };

  const deletePage = (pageId: string) => setState(prev => ({ ...prev, pages: prev.pages.filter(p => p.id !== pageId) }));

  // --- IMPROVED IMPORT FUNCTIONALITY ---
  const validateCSVImport = (csv: string) => {
      const result: { success: boolean, errors: string[], warnings: string[], parsedCount: number, data: any } = {
          success: false,
          errors: [],
          warnings: [],
          parsedCount: 0,
          data: null
      };

      try {
        const lines = csv.split('\n');
        if (lines.length < 2) {
            result.errors.push("Plik CSV jest pusty lub nie zawiera danych (minimum nagłówek i jeden wiersz).");
            return result;
        }

        const firstLineCols = lines[0].split(';');
        if (firstLineCols.length < 3) {
            result.errors.push("Niewłaściwy format pliku. Oczekiwany format to wartości oddzielone średnikami (;).");
            return result;
        }

        const startIndex = lines[0].toLowerCase().includes('strona') ? 1 : 0;
        
        const structure: Record<string, Record<string, any[]>> = {};
        let parsedCount = 0;

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            const cols = line.split(';').map(c => c.trim());
            
            if (cols.length < 3) {
                result.warnings.push(`Pominięto wiersz ${i + 1}: Zbyt mało kolumn.`);
                continue;
            }

            const pageName = cols[0] || 'Kolekcja';
            const sectionName = cols[1] || 'Inne';
            const prodName = cols[2];
            const price = cols[3] || '0';
            const desc = cols[4] || '';
            const img = cols[5] || '';

            if (!prodName) {
                result.warnings.push(`Pominięto wiersz ${i + 1}: Brak nazwy produktu.`);
                continue;
            }

            if (!structure[pageName]) structure[pageName] = {};
            if (!structure[pageName][sectionName]) structure[pageName][sectionName] = [];

            structure[pageName][sectionName].push({
                id: `imp-${Date.now()}-${i}`,
                title: prodName,
                description: desc,
                price: price,
                imageUrl: img || "https://images.unsplash.com/photo-1518837695005-2083093ee35b",
                shippingSize: 'parcel_locker'
            });
            parsedCount++;
        }

        result.parsedCount = parsedCount;
        result.data = structure;

        // Check for significant differences in product count
        const currentProductsCount = state.pages.reduce((acc, p) => 
            acc + p.sections.reduce((sAcc, s) => sAcc + (s.items?.length || 0), 0), 0
        );

        if (parsedCount === 0) {
            result.errors.push("W pliku nie znaleziono żadnych prawidłowych produktów.");
            return result;
        }

        if (currentProductsCount > 0) {
            if (parsedCount < currentProductsCount * 0.5) {
                result.warnings.push(`IMPORT ZNACZNIE ZMNIEJSZY LICZBĘ PRODUKTÓW: W pliku jest tylko ${parsedCount} produktów, a obecnie masz ${currentProductsCount}. Jeśli wybierzesz opcję "Zastąp", ponad połowa bazy zniknie.`);
            } else if (parsedCount > currentProductsCount * 2) {
                result.warnings.push(`UWAGA: W pliku znajduje się dwukrotnie więcej produktów (${parsedCount}) niż masz obecnie (${currentProductsCount}). Może to oznaczać powielone wpisy.`);
            }
        }

        result.success = true;
        return result;
      } catch (e) {
          result.errors.push("Krytyczny błąd podczas przetwarzania pliku CSV.");
          return result;
      }
  };

  const applyCSVImport = (structure: any, mode: 'replace' | 'append') => {
      setState(prev => {
          const updatedPages = mode === 'replace' ? [] : [...prev.pages];

          Object.keys(structure).forEach(pageName => {
              let pageIndex = updatedPages.findIndex(p => p.title.toLowerCase() === pageName.toLowerCase());
              
              if (pageIndex === -1) {
                  // Keep ID of existing page if replacing, else create new
                  const existingPage = prev.pages.find(p => p.title.toLowerCase() === pageName.toLowerCase());
                  const newPageId = existingPage ? existingPage.id : `page-${Date.now()}-${Math.random()}`;
                  
                  const newPage: Page = {
                      id: newPageId,
                      title: pageName,
                      slug: `/${pageName.toLowerCase().replace(/ /g, '-').replace(/[^\w-]/g, '')}`,
                      metaDescription: `Produkty z kategorii ${pageName}`,
                      sections: []
                  };
                  updatedPages.push(newPage);
                  pageIndex = updatedPages.length - 1;
              }

              const page = updatedPages[pageIndex];
              const sectionsData = structure[pageName];

              Object.keys(sectionsData).forEach(secName => {
                  let secIndex = page.sections.findIndex(s => s.title?.toLowerCase() === secName.toLowerCase() && s.type === SectionType.PRODUCT_GRID);
                  
                  if (secIndex === -1) {
                      const existingSection = mode === 'replace' 
                          // try to retrieve existing section ID from old state if present
                          ? prev.pages.find(p => p.title.toLowerCase() === pageName.toLowerCase())?.sections.find(s => s.title?.toLowerCase() === secName.toLowerCase() && s.type === SectionType.PRODUCT_GRID)
                          : null;
                      
                      page.sections.push({
                          id: existingSection ? existingSection.id : `sec-${Date.now()}-${Math.random()}`,
                          type: SectionType.PRODUCT_GRID,
                          title: secName,
                          subtitle: 'Zaimportowane produkty',
                          items: []
                      });
                      secIndex = page.sections.length - 1;
                  }

                  const existingItems = mode === 'replace' ? [] : (page.sections[secIndex].items || []);
                  page.sections[secIndex].items = [...existingItems, ...sectionsData[secName]];
              });
          });

          // In replace mode, we might want to preserve legal pages (regulamin, itp.)
          if (mode === 'replace') {
             const customPages = prev.pages.filter(p => !Object.keys(structure).some(k => k.toLowerCase() === p.title.toLowerCase()));
             // Only append those that are not product grids (e.g. Home, Terms, Privacy)
             customPages.forEach(cp => {
                 if (cp.slug === '/' || cp.slug === '/regulamin' || cp.slug === '/polityka-prywatnosci' || cp.sections.every(s => s.type !== SectionType.PRODUCT_GRID)) {
                     // If it's a page we are preserving, and it's Home, we might want to just keep it as is.
                     updatedPages.push(cp);
                 }
             });
          }

          return { ...prev, pages: updatedPages };
      });
  };

  const restoreDefaults = () => {
      setState(prev => {
          // Check if Terms and Privacy exist
          const hasTerms = prev.pages.some(p => p.slug === '/regulamin');
          const hasPrivacy = prev.pages.some(p => p.slug === '/polityka-prywatnosci');
          
          if (hasTerms && hasPrivacy) {
              alert("Strony prawne już istnieją.");
              return prev;
          }

          const newPages = [...prev.pages];
          
          if (!hasTerms) {
              const defaultTerms = INITIAL_STATE.pages.find(p => p.slug === '/regulamin');
              if (defaultTerms) newPages.push({ ...defaultTerms, id: `terms-${Date.now()}` });
          }
          
          if (!hasPrivacy) {
              const defaultPrivacy = INITIAL_STATE.pages.find(p => p.slug === '/polityka-prywatnosci');
              if (defaultPrivacy) newPages.push({ ...defaultPrivacy, id: `privacy-${Date.now()}` });
          }

          return { ...prev, pages: newPages };
      });
  };

  const loadState = (newState: AppState) => setState(newState);

  return (
    <AppContext.Provider value={{
      ...state,
      isAdminLoggedIn,
      login,
      logout,
      setPrimaryColor,
      setSecondaryColor,
      updateConfig,
      toggleEditMode,
      updatePage,
      updateSection,
      updateProduct,
      duplicateProduct,
      getProductById,
      getAllProducts,
      addPage,
      addSection,
      deleteSection,
      deletePage,
      loadState,
      applyTheme,
      addTheme,
      deleteTheme,
      addToCart,
      removeFromCart,
      toggleCart,
      clearCart,
      fetchOrders,
      updateOrderStatus,
      validateCSVImport,
      applyCSVImport,
      restoreDefaults,
      syncWithServer,
      isLoading,
      favorites,
      recentlyViewed,
      toggleFavorite,
      addRecentlyViewed
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};
